import React from 'react';
import { motion } from 'framer-motion';
import { Github, ExternalLink, Cpu, Bot, Database, Braces, LayoutDashboard, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const Projects = () => {
  const { t } = useTranslation();
  
  const projects = [
    {
      title: t('projects.items.0.title'),
      description: t('projects.items.0.description'),
      icon: <Bot className="w-6 h-6 text-cyan-400" />,
      percentage: t('projects.items.0.percentage'),
      achievements: [
        t('projects.items.0.achievements.0'),
        t('projects.items.0.achievements.1'),
        t('projects.items.0.achievements.2')
      ],
      tech: ['Power Automate', 'OpenAI API', 'Azure AI Services', 'Python', 'FastAPI']
    },
    {
      title: t('projects.items.1.title'),
      description: t('projects.items.1.description'),
      icon: <Cpu className="w-6 h-6 text-cyan-400" />,
      percentage: t('projects.items.1.percentage'),
      achievements: [
        t('projects.items.1.achievements.0'),
        t('projects.items.1.achievements.1'),
        t('projects.items.1.achievements.2')
      ],
      tech: ['PowerApps', 'Power Automate', 'SQL Server', 'SharePoint', 'Python']
    },
    {
      title: t('projects.items.2.title'),
      description: t('projects.items.2.description'),
      icon: <LayoutDashboard className="w-6 h-6 text-cyan-400" />,
      percentage: t('projects.items.2.percentage'),
      achievements: [
        t('projects.items.2.achievements.0'),
        t('projects.items.2.achievements.1'),
        t('projects.items.2.achievements.2')
      ],
      tech: ['Power BI', 'SQL Server', 'Azure Data Factory', 'Python', 'Pandas']
    },
    {
      title: t('projects.items.3.title'),
      description: t('projects.items.3.description'),
      icon: <Globe className="w-6 h-6 text-cyan-400" />,
      percentage: t('projects.items.3.percentage'),
      achievements: [
        t('projects.items.3.achievements.0'),
        t('projects.items.3.achievements.1'),
        t('projects.items.3.achievements.2')
      ],
      tech: ['React.js', 'Node.js', 'SQL Server', 'Supabase', 'Next.js', 'Python']
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl font-semibold text-center mb-16 text-slate-900 dark:text-gray-100"
        >
          {t('projects.title')}
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white dark:bg-gray-800 rounded-xl p-6 border border-slate-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4 mb-4">
                {project.icon}
                <h3 className="text-xl font-semibold text-slate-900 dark:text-gray-100">
                  {project.title} <span className="text-sm ml-2 opacity-70">({project.percentage})</span>
                </h3>
              </div>

              <p className="text-slate-600 dark:text-gray-300 mb-4">{project.description}</p>
              
              <ul className="mb-4 space-y-2">
                {project.achievements.map((achievement, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600 dark:text-gray-300 text-sm">
                    <span className="text-sky-600 dark:text-cyan-400 mt-1 flex-shrink-0">✓</span>
                    <span>{achievement}</span>
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="text-sm text-slate-700 dark:text-gray-300 font-medium mb-1 w-full">{t('projects.techStack')}:</span>
                {project.tech.map(tech => (
                  <span
                    key={tech}
                    className="px-2 py-1 text-xs rounded-full bg-slate-50 dark:bg-gray-700/50 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-600"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};